import { hoverTooltip } from '@adornis/popover/hover-tooltip.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { constructValue } from '@adornis/baseql/entities/construct.js';
import type { EntityData } from '@adornis/baseql/entities/types.js';
import { subscribeByID } from '@adornis/baseql/operations/mongo.js';
import { type NarrowedEntity, selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { guardDialog } from '@adornis/dialog/dialog-functions.js';
import { DateTimeAspect } from '@adornis/filter/aspect-schemas/DateTimeAspect.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { DropdownSelectionInput } from '@adornis/formfield/fields/dropdown-selection-input.js';
import { TextInput } from '@adornis/formfield/fields/text-input.js';
import { type FunctionalForm, useForm } from '@adornis/forms/use-form.js';
import { stateful, useMemo } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { webcomponent } from '@adornis/functional-lit/webcomponent.js';
import { CardHeader } from '@adornis/components/client/CardHeader.js';
import { PageFooter } from '@martinus/styles/client/PageFooter.js';
import { PageLoader } from '@martinus/styles/client/PageLoader.js';
import { html, nothing } from 'lit';
import { of } from 'rxjs';
import { GroupsDetailsAPI } from '../../../groups/details/api/GroupsDetailsAPI.js';
import { Group } from '../../../groups/entities/Group.js';
import { MicrosoftAPI } from '../../api/MicrosoftAPI.js';
import { buildGroupDisplayName, buildGroupMailNickname } from '../../api/microsoftGroupValues.js';
import { MicrosoftGroupState } from '../../entities/MicrosoftGroupState.js';
import { formatValue, outOfSyncRows } from '../../api/entraComparison.js';
import { type EntraComparisonRow, EntraComparisonTable, EntraSyncBadge } from '../entra-comparison.js';
import { MicrosoftGroupNotInEntra } from './sg-microsoft-entity-no-in-entra.js';
import {
  MICROSOFT_TAB_LAYOUT,
  MicrosoftSyncIndicator,
  type MicrosoftSyncStatus,
} from './sg-microsoft-sync-indicator.js';

const GROUP_SELECTION_SET = {
  _id: 1,
  _class: 1,
  name: 1,
  parentIDs: 1,
  parents: { name: 1 },
  microsoftGroupData: {
    microsoftID: 1,
    createdInEntra: 1,
    mailNickname: 1,
    description: 1,
    secureGroup: 1,
    visibility: 1,
  },
} as const;

type MicrosoftGroup = NarrowedEntity<Group, typeof GROUP_SELECTION_SET>;

/**
 * The snapshot fields the comparison reads. Declared structurally rather than as the entity, because the
 * subscription hands back a narrowed entity - see the same type on the person side.
 */
type MicrosoftGroupStateView = MicrosoftGroupSnapshot & Pick<MicrosoftGroupState, 'createdAt'>;

type MicrosoftGroupSnapshot = Pick<
  MicrosoftGroupState,
  | 'microsoftID'
  | 'createdInEntra'
  | 'displayName'
  | 'mailNickname'
  | 'description'
  | 'securityEnabled'
  | 'visibility'
  | 'memberIDs'
>;

/**
 * The values this group is supposed to have in Entra.
 *
 * ! Built with the same helpers the server writes with. !
 * The tab used to derive the display name from the bare group name while the server wrote the name
 * prefixed with the school, so every school class showed as permanently out of sync and repairing it
 * changed nothing - the server already considered the name correct.
 * @param group the group as our database holds it
 */
function desiredValuesOf(group: Maybe<MicrosoftGroup>) {
  const displayName = buildGroupDisplayName(group?.name, group?.parents?.at(0)?.name);

  return {
    displayName,
    mailNickname: buildGroupMailNickname(displayName, group?.microsoftGroupData?.mailNickname),
  };
}

/**
 * Every value the group has on both sides.
 *
 * ! Everything the tab shows about sync state is derived from this list. ! The header badge, the
 * checkmarks and the repair button all read it, so they cannot disagree - which they did: the old badge
 * compared the group's description, which had no row at all, and skipped the display name, which did.
 * @param group the group as our database holds it
 * @param state the snapshot of what Entra holds
 * @param participants how many people currently hold an active booking for the group
 */
function buildGroupComparisonRows(
  group: Maybe<MicrosoftGroup>,
  state: Maybe<MicrosoftGroupSnapshot>,
  participants: number,
): EntraComparisonRow[] {
  const local = group?.microsoftGroupData;
  const desired = desiredValuesOf(group);

  return [
    { label: 'MicrosoftID', local: local?.microsoftID, entra: state?.microsoftID },
    { label: 'In Entra erstellt am', local: local?.createdInEntra, entra: state?.createdInEntra },
    { label: 'Anzeigename', local: desired.displayName, entra: state?.displayName },
    { label: 'Mail-Nickname', local: desired.mailNickname, entra: state?.mailNickname },
    { label: 'Beschreibung', local: local?.description, entra: state?.description },
    { label: 'Sicherheitsgruppe', local: !!local?.secureGroup, entra: !!state?.securityEnabled },
    { label: 'Sichtbarkeit', local: local?.visibility ?? 'Public', entra: state?.visibility },
    { label: 'Anzahl der Nutzer', local: participants, entra: state?.memberIDs?.length ?? 0 },
  ];
}

/**
 * The left card: what our database holds. Editable, because this side is the master record - Entra is
 * brought in line with it, never the other way round.
 */
const PortalCard = stateful(
  (props: { group: Maybe<MicrosoftGroup>; participants: number; form: FunctionalForm<EntityData<Group>> }) => {
    const form = props.form;
    const desired = desiredValuesOf(props.group);

    return html`
      <div class="card card-body flex-1">
        ${CardHeader({
          title: 'Im Portal',
          subtitle: 'Diese Werte sind maßgeblich und werden nach Entra übertragen.',
        })}

        <dl class="grid grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)] items-center gap-x-3 gap-y-2">
          <dt class="text-sm font-semibold text-base-content/70">MicrosoftID</dt>
          <dd class="min-w-0 text-base-content">${formatValue(props.group?.microsoftGroupData?.microsoftID)}</dd>

          <dt class="text-sm font-semibold text-base-content/70">In Entra erstellt am</dt>
          <dd class="min-w-0 text-base-content">${formatValue(props.group?.microsoftGroupData?.createdInEntra)}</dd>

          <dt class="text-sm font-semibold text-base-content/70">Anzeigename</dt>
          <dd class="min-w-0 text-base-content">${formatValue(desired.displayName)}</dd>

          <dt class="text-sm font-semibold text-base-content/70">Mail-Nickname</dt>
          <dd class="flex min-w-0 items-center gap-2">
            ${TextInput({
              ...form.field('microsoftGroupData', 'mailNickname'),
              placeholder: 'Kein manueller Name',
              placeholderMode: 'hide',
            })}
            ${
              props.group?.microsoftGroupData?.mailNickname
                ? nothing
                : html`
                    <div
                      class="tooltip tooltip-left shrink-0"
                      data-tip="Ohne manuellen Nickname wird der Anzeigename verwendet, bei Klassen mit dem Namen der Schule davor. Aktuell: ${desired.mailNickname}"
                      ${hoverTooltip('left')}
                    >
                      <span class="icon-[material-symbols--info-outline] text-lg text-base-content/70"></span>
                    </div>
                  `
            }
          </dd>

          <dt class="text-sm font-semibold text-base-content/70">Sicherheitsgruppe</dt>
          <dd class="min-w-0">
            ${DropdownSelectionInput<boolean, false>({
              ...form.field('microsoftGroupData', 'secureGroup'),
              selectables: [true, false],
              renderString: value => (value ? 'Ja' : 'Nein'),
              placeholder: 'Keine Sicherheit ausgewählt (Nein)',
              placeholderMode: 'hide',
              emptyState: 'Keine Auswahl gefunden',
            })}
          </dd>

          <dt class="text-sm font-semibold text-base-content/70">Sichtbarkeit</dt>
          <dd class="min-w-0">
            ${DropdownSelectionInput<'Private' | 'Public', false>({
              ...form.field('microsoftGroupData', 'visibility'),
              selectables: ['Private', 'Public'],
              placeholder: 'Keine Sichtbarkeit ausgewählt (Public)',
              placeholderMode: 'hide',
              emptyState: 'Keine Auswahl gefunden',
            })}
          </dd>

          <dt class="text-sm font-semibold text-base-content/70">Anzahl der Nutzer</dt>
          <dd class="min-w-0 text-base-content">${formatValue(props.participants)}</dd>
        </dl>
      </div>
    `;
  },
);

/** The right card: what Entra holds, and how it differs. @see buildGroupComparisonRows */
const EntraCard = stateful(
  (props: {
    groupID: Maybe<string>;
    state: Maybe<MicrosoftGroupStateView>;
    rows: EntraComparisonRow[];
    differences: EntraComparisonRow[];
    loading: boolean;
  }) => {
    const { state, rows, differences, loading, groupID } = props;

    return html`
      <div class="card card-body flex-1 ${loading || !differences.length ? '' : 'border-error/40 bg-error/5'}">
        ${CardHeader({
          title: html`<span class="flex items-center gap-2">Entra ${EntraSyncBadge(rows, loading)}</span>`,
          subtitle: state?.createdAt ? html`Stand: ${formatValue(state.createdAt)}` : 'Noch kein Abgleich',
          actionsDivider: true,
          actions: html`
            <button
              class="btn btn-ghost btn-sm btn-square"
              title="Status neu von Entra laden"
              ?disabled=${!groupID}
              @click=${() => guardDialog(() => MicrosoftAPI.refreshStateForGroup(groupID ?? '')())}
            >
              <span class="icon-[material-symbols--refresh]"></span>
            </button>
          `,
        })}
        ${
          state
            ? nothing
            : html`
                <div role="alert" class="alert alert-warning">
                  <span class="icon-[material-symbols--warning-rounded]"></span>
                  <span>Der Status konnte nicht aus Entra gelesen werden.</span>
                </div>
              `
        }
        ${EntraComparisonTable(rows, loading)}
        ${
          !loading && differences.length && groupID
            ? html`
                <button
                  class="btn btn-primary btn-block mt-auto"
                  @click=${() => guardDialog(() => MicrosoftAPI.repairGroup(groupID)())}
                >
                  <span class="icon-[material-symbols--build]"></span>
                  Gruppe reparieren
                </button>
              `
            : nothing
        }
      </div>
    `;
  },
);

export const SGMicrosoftGroupControl = webcomponent(
  'sg-microsoft-group-control',
  (props: { groupID: Maybe<string> }) => {
    const group = useQuery(
      () => (props.groupID ? subscribeByID(Group, props.groupID)(GROUP_SELECTION_SET) : of(null)),
      [props.groupID],
    );

    const { value: participantCount, loading: participantsLoading } = useQuery(
      () =>
        props.groupID
          ? GroupsDetailsAPI.subscribeMembersCount(
              new AdornisFilter({
                aspects: [
                  ...GroupsDetailsAPI.getFilterAspects({ groupID: props.groupID }),
                  // ! only today's members, or this row can never match. !
                  // Entra holds the people who are in the group *now*, and the sync fills it from the
                  // bookings that are active today. Without this aspect the count included every
                  // booking the group ever had, so any group with former members showed a difference
                  // that no repair could resolve. The previous code passed `active: true`, which the
                  // aspect builder does not read.
                  new DateTimeAspect({
                    customID: 'activeOnDate',
                    mode: 'is',
                    firstValue: new Date(),
                    secondValue: new Date(),
                  }),
                ],
              }),
            )()
          : of(null),
      [props.groupID],
    );

    // the form edits a plain snapshot; `constructValue` turns it back into the entity before saving.
    // It lives here rather than in the card so the save bar can sit next to the card container.
    const groupData = useMemo(() => [(group.value?.toObject() ?? {}) as EntityData<Group>], [group.value?._id]);
    const form = useForm(groupData, { validator: Group.prototype.validator.functions }, [groupData]);

    // one subscription for the whole tab: the card and the indicator between the cards have to answer
    // the same question, so they read the same rows rather than each deriving their own
    const { value: state, loading: stateLoading } = useQuery(
      () =>
        props.groupID
          ? MicrosoftAPI.subscribeStateForGroup(props.groupID)(selectionSet(() => MicrosoftGroupState, 1))
          : of(null),
      [props.groupID],
    );

    if (group.loading || participantsLoading)
      return PageLoader({ cardOptions: [{ title: 'Im Portal' }, { title: 'Entra' }] });

    if (group.value && !group.value.microsoftGroupData?.microsoftID) {
      return MicrosoftGroupNotInEntra({ group: group.value });
    }

    if (!group.value?.microsoftGroupData) return nothing;

    const rows = buildGroupComparisonRows(group.value, state, participantCount ?? 0);
    const differences = outOfSyncRows(rows);
    const status: MicrosoftSyncStatus = stateLoading ? 'loading' : differences.length ? 'drift' : 'synced';

    const handleSave = async () => {
      const validationError = await form.validate();
      if (validationError) return;

      await guardDialog(async () => {
        await constructValue<Group>(form.document).save();
      });
    };

    /**
     * ! The save bar is a sibling of the card container, never a child of a card. !
     *
     * It is `position: fixed` and sized with `-webkit-fill-available`, which resolves against its
     * containing block - nested inside a card it took on the card's width and sat inside the card
     * container's margin instead of spanning the viewport. Every other page in this app renders it
     * the same way, next to the container rather than inside the content.
     */
    return html`
      <div class="card-container ${MICROSOFT_TAB_LAYOUT}">
        ${PortalCard({ group: group.value, participants: participantCount ?? 0, form })}
        ${MicrosoftSyncIndicator({ status, differences: differences.length })}
        ${EntraCard({ groupID: props.groupID, state, rows, differences, loading: stateLoading })}
      </div>

      ${PageFooter({
        content: html`
          <span class="text-sm text-base-content/70">Änderungen werden nach dem Speichern übertragen.</span>
          <button class="btn btn-primary btn-lg" @click=${handleSave}>
            <span class="icon-[material-symbols--save]"></span>
            Speichern
          </button>
        `,
      })}
    `;
  },
);
